import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }

function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

import React, { useCallback, useRef, useState } from 'react';
import { arrayOf, bool, func, node, number, shape, string } from 'prop-types';
import { Checkbox, Box, Flex, Button, Text } from '../../atoms';
import { ReviewInput } from '../../molecules';
import { Row } from './review-form.styles';

function ReviewForm({
  agreedError,
  agreedLabel,
  buttonLabel,
  onSubmit,
  ratingError,
  ratingLabel,
  recommendedError,
  recommendedLabel,
  reviewCharacteristics,
  minTextSize,
  textError,
  textLabel,
  textPlaceholder,
  titleError,
  titleLabel,
  titlePlaceholder,
  focus
}) {
  const [agreed, setAgreed] = useState(false);
  const [recommended, setRecommended] = useState('');
  const [rating, setRating] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [characteristics, setCharacteristics] = useState(reviewCharacteristics || []);
  const [errorsValidation, setErrorsValidation] = useState({});
  const refs = Object.assign({
    rating: useRef(),
    title: useRef(),
    text: useRef(),
    recommended: useRef()
  }, characteristics.reduce((acc, {
    id
  }) => Object.assign({}, acc, {
    [id]: useRef()
  }), {}), {
    agreed: useRef()
  });
  const updateErrors = useCallback((_key, _error, _focus = focus) => {
    setErrorsValidation(_ref => {
      let errs = _objectWithoutPropertiesLoose(_ref, [_key].map(_toPropertyKey));

      const errors = Object.assign({}, errs, _error);
      const keys = Object.keys(errors);

      if (_focus && keys.includes(_key) && _error) {
        const {
          current
        } = refs[keys[0]];
        window.scroll({
          top: current.offsetTop - 35,
          left: 0,
          behavior: 'smooth'
        });
      }

      return errors;
    });
  }, [errorsValidation]);
  const validateLength = useCallback((_value, _key, _length, _message, _focus) => {
    const error = _value.length < _length ? {
      [_key]: _message
    } : undefined;
    updateErrors(_key, error, _focus);
    return error;
  }, [rating, title, text, recommended, characteristics, agreed]);
  const validatePresence = useCallback((_value, _key, _message, _focus) => {
    const error = !_value ? {
      [_key]: _message
    } : undefined;
    updateErrors(_key, error, _focus);
    return error;
  }, [rating, title, text, recommended, characteristics, agreed]);
  const clearForm = useCallback(() => {
    setRating('');
    setTitle('');
    setText('');
    setRecommended('');
    setAgreed(false);
    setCharacteristics(reviewCharacteristics.map(_ref2 => {
      let rest = _objectWithoutPropertiesLoose(_ref2, ["value"]);

      return rest;
    }));
  }, [reviewCharacteristics]);
  const handleSubmit = useCallback(e => {
    const errors = Object.assign({}, validatePresence(rating, 'rating', ratingError), validatePresence(title, 'title', titleError), validateLength(text, 'text', minTextSize, textError), validatePresence(recommended, 'recommended', recommendedError), characteristics.reduce((acc, characteristic) => Object.assign({}, acc, validatePresence(characteristic.value, characteristic.id, ratingError)), {}), validatePresence(agreed, 'agreed', agreedError));

    if (!Object.keys(errors).length) {
      onSubmit({
        rating,
        title,
        text,
        recommended,
        characteristics
      }, clearForm);
    }

    e.preventDefault();
  }, [rating, title, text, recommended, agreed, characteristics]);
  return /*#__PURE__*/React.createElement(Box, {
    as: "form",
    "data-testid": "review-form"
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(ReviewInput, {
    onChange: e => {
      validatePresence(e.target.value, 'rating', ratingError, false);
      setRating(e.target.value);
    },
    ref: refs.rating,
    value: rating,
    error: errorsValidation.rating,
    type: "rating",
    label: ratingLabel,
    height: [40, 30],
    width: [40, 30],
    gap: [0, 30],
    fontSize: [4, 2],
    fontWeight: ['bold', 'regular'],
    flexDirection: ['column', 'row'],
    alignItems: "center",
    textAlign: ['center', 'right'],
    name: "rating"
  })), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(ReviewInput, {
    onChange: e => setTitle(e.target.value),
    onFocus: () => setErrorsValidation(_ref3 => {
      let errors = _objectWithoutPropertiesLoose(_ref3, ["title"]);

      return errors;
    }),
    onBlur: e => validatePresence(e.target.value, 'title', titleError, false),
    ref: refs.title,
    value: title,
    error: errorsValidation.title,
    type: "text",
    label: titleLabel,
    placeholder: titlePlaceholder,
    gap: [0, 30],
    flexDirection: ['column', 'row'],
    textAlign: ['left', 'right']
  })), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(ReviewInput, {
    onChange: e => setText(e.target.value),
    onFocus: () => setErrorsValidation(_ref4 => {
      let errors = _objectWithoutPropertiesLoose(_ref4, ["text"]);

      return errors;
    }),
    onBlur: e => validateLength(e.target.value, 'text', 50, textError, false),
    ref: refs.text,
    value: text,
    error: errorsValidation.text,
    type: "textarea",
    label: textLabel,
    gap: [0, 30],
    flexDirection: ['column', 'row'],
    textAlign: ['left', 'right'],
    placeholder: textPlaceholder
  })), /*#__PURE__*/React.createElement(Row, {
    borderBottomWidth: ['1px', '0px'],
    borderBottomStyle: "solid",
    borderColor: "lightSilver"
  }, /*#__PURE__*/React.createElement(ReviewInput, {
    onChange: e => {
      validatePresence(e.target.value, 'recommended', recommendedError, false);
      setRecommended(e.target.value);
    },
    ref: refs.recommended,
    value: recommended,
    error: errorsValidation.recommended,
    type: "recommendation",
    label: recommendedLabel,
    gap: [0, 30],
    flexDirection: ['column', 'row'],
    alignItems: "center",
    textAlign: ['center', 'right']
  })), characteristics == null ? void 0 : characteristics.map(({
    id,
    label,
    value
  }) => /*#__PURE__*/React.createElement(Row, {
    key: id,
    borderBottomWidth: ['1px', '0px'],
    borderBottomStyle: "solid",
    borderColor: "lightSilver"
  }, /*#__PURE__*/React.createElement(ReviewInput, {
    onChange: e => {
      validatePresence(e.target.value, id, ratingError, false);
      setCharacteristics(characteristics.map(characteristic => {
        if (characteristic.id === id) {
          return Object.assign({}, characteristic, {
            value: e.target.value
          });
        }

        return characteristic;
      }));
    },
    name: id,
    ref: refs[id],
    value: value,
    error: errorsValidation[id],
    label: label,
    type: "rating",
    height: [30, 24],
    width: [30, 24],
    gap: [0, 30],
    flexDirection: ['column', 'row'],
    alignItems: "center",
    textAlign: ['center', 'right']
  }))), /*#__PURE__*/React.createElement(Flex, {
    m: 20,
    flexDirection: ['column', 'row'],
    gap: [0, 30]
  }, /*#__PURE__*/React.createElement(Flex, {
    flex: "0.75"
  }), /*#__PURE__*/React.createElement(Flex, {
    flex: "1",
    gap: "2",
    alignItems: ['center', 'start'],
    justifyContent: "space-evenly",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    ref: refs.agreed,
    checked: agreed,
    onChange: e => {
      validatePresence(e.target.checked, 'agreed', agreedError, false);
      setAgreed(e.target.checked);
    }
  }, agreedLabel), errorsValidation.agreed && /*#__PURE__*/React.createElement(Text, {
    color: "error.base"
  }, errorsValidation.agreed))), /*#__PURE__*/React.createElement(Flex, {
    mY: 20,
    flexDirection: ['column', 'row'],
    gap: [0, 30]
  }, /*#__PURE__*/React.createElement(Flex, {
    flex: "0.75"
  }), /*#__PURE__*/React.createElement(Flex, {
    flex: "1",
    gap: "2",
    alignItems: ['center', 'start'],
    justifyContent: "space-evenly",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubmit,
    full: true
  }, buttonLabel))));
}

ReviewForm.defaultProps = {
  agreedError: 'Por favor verifique os termos.',
  agreedLabel: 'Concordo com os termos e condições',
  buttonLabel: 'Enviar avaliação',
  ratingError: 'Clique nas estrelas para classificar!',
  ratingLabel: 'Avaliação Geral',
  recommendedError: 'Por favor selecione uma opção.',
  recommendedLabel: 'Você recomenda esse produto?',
  minTextSize: 50,
  textError: 'Mínimo de 50 caracteres.',
  textLabel: 'Avaliação',
  titleError: 'Insira o título completo corretamente.',
  titleLabel: 'Título da avaliação',
  titlePlaceholder: 'Ex. Produto Maravilhoso',
  textPlaceholder: 'Ex: Escreva exclusivamente sobre o produto. Para sugestões, reclamações ou problemas em geral, acesse nossa Central de Atendimento',
  focus: true
};
process.env.NODE_ENV !== "production" ? ReviewForm.propTypes = {
  agreedError: string,
  agreedLabel: node,
  buttonLabel: string,
  minTextSize: number,
  onSubmit: func,
  ratingError: string,
  ratingLabel: string,
  recommendedError: string,
  recommendedLabel: string,
  textError: string,
  textLabel: string,
  textPlaceholder: string,
  titleError: string,
  titleLabel: string,
  titlePlaceholder: string,
  reviewCharacteristics: arrayOf(shape({
    id: string,
    label: string
  })),
  focus: bool
} : void 0;
export default ReviewForm;